import moment from "moment-timezone";
import { useState } from "react";
import type { RangeValue } from "rc-picker/lib/interface";

export interface MultiFunctionDateFilterProps {
  setFromDate: (date: string | null) => void;
  setToDate: (date: string | null) => void;
}

export const useMultiFunctionDateFilter = (props: MultiFunctionDateFilterProps) => {
  /* STATE */
  const [selectedOption, setSelectedOption] = useState<string | null>("Last N Days");
  const [showLastNDays, setShowLastNDays] = useState<boolean>(true);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState<boolean>(false);
  const [showAllMonths, setShowAllMonths] = useState<boolean>(false);
  const [selectedDays, setSelectedDays] = useState<number | null>(30);

  /* HANDLERS */

  // Handle Last N Days Click
  const handleLastNDaysClick = (days: number) => {
    let from;
    let to = moment();

    if (days === 1) {
      from = moment().startOf("day"); // today 00:00
    } else {
      from = moment().subtract(days, "days").startOf("day"); // n days ago, 00:00
    }

    props?.setFromDate(from.format("DD-MM-YYYY HH:mm"));
    props?.setToDate(to.format("DD-MM-YYYY HH:mm"));
    setSelectedDays(days);
  };

  // Handle Custom Date Picker Selection
  const handleCustomDateSelection = (dates: RangeValue<moment.Moment>, formatStrings: [string, string]) => {
    if (dates && dates[0] && dates[1]) {
      const from = dates[0];
      let to = dates[1];

      // If the selected end date (toDate) is today, restrict it to the current time
      if (to.isSame(moment(), "day") && to.isAfter(moment())) {
        to = moment(); // Set toDate to the current moment
      }

      props.setFromDate(from.format("DD-MM-YYYY HH:mm"));
      props.setToDate(to.format("DD-MM-YYYY HH:mm"));
    }
  };

  // Handle Month Selection
  const handleMonthSelection = (month: moment.Moment) => {
    // Ensure 'from' is the start of the selected month
    const from = month.clone().startOf("month");

    // Ensure 'to' is the end of the selected month, but not beyond the current date/time
    let to = month.clone().endOf("month");

    if (month.isSame(moment(), "month")) {
      to = moment(); // Use the current date/time if it's the current month
    }

    // Update the props with the formatted dates
    props?.setFromDate(from.format("DD-MM-YYYY HH:mm"));
    props?.setToDate(to.format("DD-MM-YYYY HH:mm"));
  };

  // Generate months list with current month at the top
  const getMonthsList = () => {
    const months = [];
    const currentMonth = moment().startOf("month");
    for (let i = 0; i < 12; i++) {
      months.push({
        value: currentMonth.clone().subtract(i, "months").format("YYYY-MM"),
        label: currentMonth.clone().subtract(i, "months").format("MMMM YYYY")
      });
    }
    return months;
  };

  const resetInitialValue = () => {
    props?.setFromDate(null);
    props?.setToDate(null);
    setSelectedOption(null);
    setSelectedDays(null);
    setShowLastNDays(false);
    setShowCustomDatePicker(false);
    setShowAllMonths(false);
  };

  const handleDropdownClick = (key: string) => {
    resetInitialValue();

    if (key === "lastNDays") {
      setShowLastNDays(true);
      setShowCustomDatePicker(false);
      setShowAllMonths(false);
      setSelectedOption("Last N Days");
    } else if (key === "customDateFilter") {
      setShowCustomDatePicker(true);
      setShowLastNDays(false);
      setShowAllMonths(false);
      setSelectedOption("Custom Date Filter");
    } else if (key === "allMonths") {
      setShowAllMonths(true);
      setShowLastNDays(false);
      setShowCustomDatePicker(false);
      setSelectedOption("All Months");
    }
  };

  /* EFFECTS */

  return {
    showLastNDays,
    showCustomDatePicker,
    showAllMonths,
    selectedOption,
    selectedDays,
    handleMonthSelection,
    getMonthsList,
    handleCustomDateSelection,
    handleLastNDaysClick,
    handleDropdownClick
  };
};
